import React from 'react';
import { Helmet } from 'react-helmet-async';

export default function Privacy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - Aramco Leads Pro</title>
        <meta name="description" content="Privacy Policy for Aramco Leads Pro CRM platform. Learn how we protect your data." />
      </Helmet>

      <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', color: 'white', padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '3rem', fontWeight: '800', marginBottom: '0.5rem' }}>Privacy Policy</h1>
          <p style={{ fontSize: '0.95rem', opacity: 0.85 }}>Last Updated: December 2, 2025</p>
        </div>
      </div>

      <section style={{ padding: '4rem 2rem', background: '#f9fafb' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', lineHeight: '1.8' }}>
          <div style={{ background: 'white', padding: '3rem', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            
            <h2 style={{ color: '#1e40af', marginBottom: '1rem', fontSize: '1.75rem' }}>Introduction</h2>
            <p style={{ marginBottom: '2rem' }}>
              Aramco 1 USA Corporation, operating as Aramco Leads Pro ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, and safeguard your information.
            </p>

            <h2 style={{ color: '#1e40af', marginBottom: '1rem', fontSize: '1.75rem' }}>Information We Collect</h2>
            <p style={{ marginBottom: '1rem' }}>We may collect:</p>
            <ul style={{ marginBottom: '2rem', paddingLeft: '2rem' }}>
              <li>Business contact information (name, email, phone)</li>
              <li>Company information</li>
              <li>Account details and preferences</li>
              <li>Customer data you input into the CRM</li>
              <li>Communication preferences and consent records</li>
              <li>Usage data and analytics</li>
            </ul>

            <h2 style={{ color: '#1e40af', marginBottom: '1rem', fontSize: '1.75rem' }}>How We Use Your Information</h2>
            <p style={{ marginBottom: '1rem' }}>We use information for:</p>
            <ul style={{ marginBottom: '2rem', paddingLeft: '2rem' }}>
              <li>Providing CRM and communication services</li>
              <li>Managing your account</li>
              <li>Sending service notifications</li>
              <li>Improving our platform</li>
              <li>Ensuring compliance</li>
              <li>Providing customer support</li>
            </ul>

            <h2 style={{ color: '#1e40af', marginBottom: '1rem', fontSize: '1.75rem' }}>Message Consent</h2>
            <div style={{ background: '#dbeafe', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem', border: '2px solid #3b82f6' }}>
              <p style={{ fontWeight: '600', color: '#1e40af', marginBottom: '0.5rem' }}>Consent Disclosure:</p>
              <p style={{ color: '#1e3a8a', marginBottom: '1rem' }}>
                By providing your contact information, you consent to receive communications from Aramco Leads Pro regarding your account and services.
                <strong> Message frequency varies. Message and data rates may apply.</strong> Reply STOP to opt out.
              </p>
              <p style={{ fontSize: '0.95rem', color: '#1e40af', fontWeight: '600' }}>
                Your consent is not required as a condition of purchase. Consent is not transferable and will not be sold or shared with third parties.
              </p>
            </div>

            <h2 style={{ color: '#1e40af', marginBottom: '1rem', fontSize: '1.75rem' }}>Lending & Financial Services Disclosure</h2>
            <div style={{ background: '#fef3c7', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem', border: '2px solid #f59e0b' }}>
              <p style={{ fontWeight: '600', color: '#92400e', marginBottom: '1rem' }}>Special Notice for Lending Services:</p>
              <p style={{ color: '#78350f', marginBottom: '1rem' }}>
                If you opt in to receive information about lending and financial services:
              </p>
              <ul style={{ paddingLeft: '2rem', color: '#78350f', marginBottom: '1rem' }}>
                <li>You consent to receive promotional messages about loan products and lending opportunities</li>
                <li>Messages may include information about interest rates, loan terms, and eligibility requirements</li>
                <li>We may share your information with licensed lending partners</li>
                <li>Receiving messages does not guarantee loan approval</li>
                <li>All loan offers are subject to credit approval and underwriting</li>
                <li>APR, terms, and conditions vary by lender and your creditworthiness</li>
                <li>You have the right to opt out at any time without affecting any existing loan or service</li>
              </ul>
              <p style={{ fontSize: '0.9rem', color: '#92400e', fontWeight: '600' }}>
                We comply with the Truth in Lending Act (TILA), Fair Lending practices, and CFPB guidelines. 
                We do not engage in predatory lending practices. For more information, see our{' '}
                <a href="/lending-compliance" style={{ color: '#92400e', textDecoration: 'underline' }}>Lending Compliance</a> page.
              </p>
            </div>

            <h2 style={{ color: '#1e40af', marginBottom: '1rem', fontSize: '1.75rem' }}>Information Sharing</h2>
            <p style={{ marginBottom: '1rem' }}>We share information only:</p>
            <ul style={{ marginBottom: '2rem', paddingLeft: '2rem' }}>
              <li><strong>Service Providers:</strong> Technical vendors assisting with platform operations</li>
              <li><strong>Lending Partners:</strong> Licensed financial institutions (only with your consent for lending services)</li>
              <li><strong>Legal Requirements:</strong> When required by law</li>
              <li><strong>Business Transfers:</strong> In connection with mergers or acquisitions</li>
              <li><strong>With Consent:</strong> When you explicitly consent</li>
            </ul>
            <p style={{ marginBottom: '2rem', fontWeight: '600', color: '#1e40af' }}>
              We do not sell, rent, or share customer data with third parties for marketing purposes without your explicit consent.
            </p>

            <h2 style={{ color: '#1e40af', marginBottom: '1rem', fontSize: '1.75rem' }}>Data Security</h2>
            <p style={{ marginBottom: '1rem' }}>We implement:</p>
            <ul style={{ marginBottom: '2rem', paddingLeft: '2rem' }}>
              <li>Encryption of data in transit and at rest</li>
              <li>Secure data centers with access controls</li>
              <li>Regular security audits</li>
              <li>Employee training on data protection</li>
              <li>Multi-factor authentication</li>
            </ul>

            <h2 style={{ color: '#1e40af', marginBottom: '1rem', fontSize: '1.75rem' }}>Your Rights</h2>
            <p style={{ marginBottom: '1rem' }}>You have the right to:</p>
            <ul style={{ marginBottom: '2rem', paddingLeft: '2rem' }}>
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Opt-out:</strong> Unsubscribe from communications</li>
              <li><strong>Data Portability:</strong> Request a copy of your data</li>
            </ul>

            <h2 style={{ color: '#1e40af', marginBottom: '1rem', fontSize: '1.75rem' }}>Contact Information</h2>
            <div style={{ background: '#f9fafb', padding: '2rem', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
              <p style={{ fontWeight: '600', marginBottom: '1rem' }}>Aramco 1 USA Corporation</p>
              <p><strong>DBA:</strong> Aramco Leads Pro</p>
              <p>11 Linden Ave<br />Kearny, NJ 07032<br />United States</p>
              <p><strong>EIN:</strong> 33-1773365</p>
              <p style={{ marginTop: '1rem' }}><strong>Email:</strong> privacy@aramcoleadspro.com</p>
              <p><strong>Support:</strong> support@aramcoleadspro.com</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
